import * as React from 'react';
import AriaModal from 'react-aria-modal';
import {withTranslation, WithTranslation} from 'react-i18next';
import {Link} from 'gatsby';

import LanguageToggle from '@src/components/Nav/LanguageToggle';
import {NavItem} from '@src/components/Nav';
import Logo from '@src/svgs/Logo';
import MobileMenu from '../MobileMenu';
import {handleExternalLinks} from '@src/utils';
import {GTMCallUs} from '@src/components/GTM/GTMCallUs';

import * as styles from './mobileNav.module.scss';

import type {Translations} from '@src/i18n';

const GTM_DEFAULT_KEYS = {
  phoneNumber: 'callUs.number',
  text: 'callUsSticky.mobile',
};

const GTM_DIRECT_KEYS = {
  phoneNumber: 'callUsDirect.number',
  text: 'callUsStickyDirect.mobile',
};

const GTM_CALL_US_PROPS = {
  defaultKeys: GTM_DEFAULT_KEYS,
  directKeys: GTM_DIRECT_KEYS,
  namespace: 'Nav',
};

interface MobileNavProps extends WithTranslation<'Nav'> {
  translations?: Translations[];
  nav: NavItem[];
}

interface MobileNavState {
  hasEntered: boolean;
  menuActive: boolean;
}

class MobileNav extends React.Component<MobileNavProps, MobileNavState> {
  constructor(props: MobileNavProps) {
    super(props);

    this.state = {
      hasEntered: false,
      menuActive: false,
    };
  }

  closeMenu = () => {
    return new Promise(resolve => {
      this.setState({hasEntered: false}, () => {
        setTimeout(() => {
          this.setState({menuActive: false}, () => resolve(undefined));
        }, 300);
      });
    });
  };

  onEnter = () => {
    this.setState({hasEntered: true});
    handleExternalLinks();
  };

  openMenu = () => {
    this.setState({menuActive: true});
  };

  render() {
    const {hasEntered, menuActive} = this.state;
    const {t, translations, nav} = this.props;

    return (
      <div className={styles.nav}>
        <nav className={styles.navBar}>
          <div className={styles.logo}>
            <Link className={styles.logoLink} to={t('links.home.url')}>
              <span className={styles.visuallyHidden}>
                {t('links.home.text')}
              </span>
              <Logo />
            </Link>
          </div>
          <button onClick={this.openMenu} className={styles.menuButton}>
            {t('mobileMenuButtonText')}
          </button>
        </nav>
        <div className={styles.menuCTA}>
          <GTMCallUs {...GTM_CALL_US_PROPS}>
            {({text, phoneNumber}) => (
              <a href={`tel:${phoneNumber}`} rel="noopener noreferrer">
                {text}
              </a>
            )}
          </GTMCallUs>
        </div>

        <AriaModal
          mounted={menuActive}
          onEnter={this.onEnter}
          onExit={this.closeMenu}
          underlayClass={`${styles.underlayAnimate} ${
            hasEntered ? styles.underlayAnimateActive : null
          }`}
          titleText={t('mobileMenuModalTitle')}>
          <div
            className={`${styles.menu} ${styles.contentAnimate} ${
              hasEntered ? styles.contentAnimateActive : null
            }`}>
            <nav className={styles.menuNav}>
              <div className={styles.menuHeader}>
                <Link
                  className={styles.logoLink}
                  to={t('links.home.url')}
                  onClick={this.closeMenu}>
                  <Logo />
                </Link>
                <button
                  className={`${styles.closeButton} ${
                    !translations ? styles.noToggle : ''
                  }`}
                  onClick={this.closeMenu}>
                  {t('mobileMenuCloseButtonText')}
                </button>
              </div>
              <MobileMenu closeMenu={this.closeMenu} nav={nav} />
            </nav>
            {translations && (
              <div className={styles.toggle}>
                <LanguageToggle translations={translations} />
              </div>
            )}
          </div>
        </AriaModal>
      </div>
    );
  }
}

export default withTranslation('Nav')(MobileNav);
